import React, { useEffect, useMemo, useState } from "react";
import "./profilepage.css";
import { useReadMemory } from "../../generic/hooks/useReadMemory";
import MemoryType from "../../../service/memory/enum/MemoryType";
import { executeIntent } from "../../utils/IntentExecutor";
import IntentType from "../../../intentexecutor/utils/IntentType";
import BackButton from "../../generic/elements/backbutton/BackButton";
import { Col, Row, Spin, notification } from "antd";
import { useRecoilValue } from "recoil";
import { currentConfiguration } from "../../../service/memory/recoil/configurationstore";
import ConfigurationConstants from "../../utils/configurationconstants";
import { useObservable } from "../../generic/hooks/useObservable";
import { mapPatientToProfileData } from "../../../components/generic/profiledataextractor/profileDataExtractor";
import ProfileOverview from "../../../components/generic/profileoverview/ProfileOverview";
import AddEditPatientModal from "../../carecoordinator/patient/AddEditPatientModal";
import CareCircleComponent from "../patientcarecircle/CareCircleComponent";
import { useNavigate, useSearchParams } from "react-router-dom";
import { RoleToLinkMapper } from "../../utils/UrlUtils";
import { useGetRole } from "../../generic/hooks/useGetRole";
import { patientDataUpdateResponse } from "../../carecoordinator/patient/AddEditPatientModal";
import CarePlan from "../../carecoordinator/careplan/CarePlan";
import ChatBoxWrapper from "../../carecoordinator/chatbox/ChatBoxWrapper";
import SectionCard from "../../generic/sectioncard/SectionCard";
import { useGetOrgIdentifier } from "../../generic/hooks/useGetOrgIdentifier";
import { officeLabel } from "../../../components/recoil/officeLabelRecoil";
import CustomModal from "../../../components/generic/custommodal/CustomModal";
import PrimaryButton from "../../../components/generic/buttons/PrimaryButton";
import SecondaryButton from "../../../components/generic/buttons/SecondaryButton";
import PatientPrefernceForm from "../../../components/forms/PatientPrefernceForm";

const PATIENT_PREFERENCES_FORM_ID = "patient-profile-preferences-form";

const PatientProfilePage = () => {
  const user = useReadMemory(MemoryType.USER);
  const organizationIdentifier = useGetOrgIdentifier();
  const [searchParams] = useSearchParams();
  const identifier = searchParams.get("identifier");
  const [userDetails, setUserDetails] = useState(undefined);
  const label = useRecoilValue(officeLabel);
  const configuration = useRecoilValue(currentConfiguration);
  const secretKey =
    configuration[ConfigurationConstants.NIRAMAYA_ORGANIZATION_SECRETKEY];
  const [refresh, setRefresh] = useState(false);
  const navigate = useNavigate();
  const role = useGetRole();
  useObservable(patientDataUpdateResponse, setRefresh);
  const [userProfileData, setUserProfileData] = useState({});
  const [allowProfileEdit, setAllowProfileEdit] = useState(false);
  const [openAddPatientModal, setOpenAddPatientModal] = useState(false);
  const [openForm, setOpenForm] = useState(false);
  const [isCareCircleAllowed, setIsCareCircleAllowed] = useState(false);
  const [patientStatusOptions, setPatientStatusOptions] = useState([]);
  const [formLoading, setFormLoading] = useState(false);
  const [patientPreferenceOptions, setPatientPreferenceOptions] =
    useState(null);

  const carePlanWorkforceIdentifier = useMemo(
    () => user?.jobTitle === "Volunteer" && user?.userIdentifier,
    [user?.jobTitle, user?.userIdentifier],
  );

  const patientPreferencesModalFooter = useMemo(
    () => (
      <>
        <SecondaryButton
          buttonText="Cancel"
          handleOnClick={() => setOpenForm(false)}
        />
        <PrimaryButton
          buttonText="Add"
          htmlType="submit"
          form={PATIENT_PREFERENCES_FORM_ID}
          loading={formLoading}
        />
      </>
    ),
    [formLoading],
  );

  useEffect(() => {
    const getUserDetails = async () => {
      const payload = {
        userIdentifier: identifier,
        secretKey: secretKey?.key,
      };
      try {
        const userData = await executeIntent(
          IntentType.GET_PATIENT_DETAILS_BY_USER_IDENTIFIER,
          payload,
        );
        setUserDetails(userData[0]);
        setRefresh(false);
      } catch (error) {
        notification.error({
          message: `Error while fetching user details`,
          placement: "bottomRight",
        });
      }
    };

    if (secretKey?.key && identifier) {
      getUserDetails();
    }
  }, [identifier, refresh, secretKey?.key]);

  useEffect(() => {
    // access control for profile edit
    const configurationAllowProfileEdit =
      configuration[ConfigurationConstants.FEATUREACCESS_PATIENT_PROFILE_EDIT];
    setAllowProfileEdit(configurationAllowProfileEdit?.visibility);

    // access control for carecircle
    const configurationAllowCareCircle =
      configuration[ConfigurationConstants.FEATUREACCESS_PATIENT_CARECIRCLE];
    setIsCareCircleAllowed(configurationAllowCareCircle?.visibility);
  }, [configuration]);

  useEffect(() => {
    const getOptions = async () => {
      const payload = {
        configurationKey: [
          "niramaya.add.patient.options",
          "niramaya.patient.preference.options",
        ],
      };
      const result = await executeIntent(
        IntentType.GET_CONFIGURATION,
        payload,
        { organizationIdentifier },
      );
      const options =
        result[0].configurationValue &&
        JSON.parse(result[0].configurationValue);
      setPatientStatusOptions(options?.patientStatus?.menuItems);

      const preferenceOptions =
        result[1].configurationValue &&
        JSON.parse(result[1].configurationValue);
      setPatientPreferenceOptions(preferenceOptions);
    };
    getOptions();
  }, [organizationIdentifier]);

  useEffect(() => {
    const actions = [
      allowProfileEdit && {
        label: "Edit",
        icon: "ICON_FOR_PROFILE_EDIT",
        map: "ICON_FOR_PROFILE_EDIT",
        functionName: () => {
          setOpenAddPatientModal(true);
        },
      },
      role === "Volunteer Coordinator" && {
        label: "Add Details",
        icon: "ICON_PREFERENCE",
        map: "ICON_PREFERENCE",
        functionName: () => {
          setOpenForm(true);
        },
      },
      role === "Volunteer Coordinator" && {
        label: "Request for Volunteer",
        icon: "REQUEST_FOR_VOLUNTEER_SERVICE",
        map: "REQUEST_FOR_VOLUNTEER_SERVICE",
        functionName: () => {
          navigate(
            RoleToLinkMapper(role) +
              "request-for-volunteering-service?identifier=" +
              identifier,
          );
        },
      },
    ].filter(Boolean);
    const patientProfile = mapPatientToProfileData(
      userDetails,
      patientStatusOptions,
      label,
      patientPreferenceOptions,
    );
    const updatePatientProfile = {
      ...patientProfile,
      actionIcons: actions,
    };
    setUserProfileData(updatePatientProfile);
  }, [
    allowProfileEdit,
    identifier,
    label,
    navigate,
    role,
    userDetails,
    patientStatusOptions,
    patientPreferenceOptions,
  ]);

  return (
    <section className="section-content">
      <div className="form-renderer-back-button">
        <BackButton />
        <p>Patient Profile Page</p>
      </div>
      <div>
        <div className="patient-profile-overview-shell">
          {userDetails !== undefined ? (
            userProfileData?.overviewConfig ? (
              <>
                <ProfileOverview
                  overviewConfig={userProfileData.overviewConfig}
                  actionIcons={userProfileData.actionIcons}
                  actionData={userProfileData.actionData}
                />
                {openAddPatientModal && (
                  <AddEditPatientModal
                    openAddPatientModal={openAddPatientModal}
                    setOpenAddPatientModal={setOpenAddPatientModal}
                    editPatient
                    userIdentifier={userDetails?.userIdentifier}
                  />
                )}
              </>
            ) : (
              <Spin className="d-flex justify-content-center m-auto py-5" />
            )
          ) : (
            <Spin className="d-flex justify-content-center m-auto py-5" />
          )}
        </div>
        <Row gutter={[20, 20]}>
          <Col xs={24} xl={12}>
            <ChatBoxWrapper patient={userDetails} />
          </Col>
          <Col xs={24} xl={12}>
            <SectionCard
              title="Volunteer Request"
              className="listCardStylingThirdRow no-scrollbar"
            >
              <CarePlan
                workforceIdentifier={carePlanWorkforceIdentifier}
                patientIdentifier={identifier}
                carePlanClass="px-3"
                spinnerClass="spinnerCenter"
              />
            </SectionCard>
          </Col>
          {isCareCircleAllowed && (
            <Col xs={24} xl={12}>
              <CareCircleComponent
                identifier={identifier}
                userDetails={userDetails}
              />
            </Col>
          )}
        </Row>
      </div>
      {openForm && (
        <CustomModal
          closable={false}
          isModalOpen={openForm}
          title="Patient Preferences"
          footer={patientPreferencesModalFooter}
          closeModal={setOpenForm}
        >
          <PatientPrefernceForm
            formId={PATIENT_PREFERENCES_FORM_ID}
            userIdentifier={identifier}
            onLoadingChange={setFormLoading}
            setOpenForm={setOpenForm}
          />
        </CustomModal>
      )}
    </section>
  );
};

export default PatientProfilePage;
